/**
 * Where this installation looks for its deployment, and where it files the credentials for it.
 *
 * ADR-050 makes every tenant a full deployment — ours, or one somebody runs themselves — and makes
 * the desktop a shallow renderer of one. The address therefore stops being a build-time fact and
 * becomes something a person names, which drags identity along with it: sign-in resolves against
 * the chosen address, so the tokens it returns belong to that address and to no other.
 *
 * **Two deployments must never share a credential slot.** A single global `featit:accessToken`
 * would mean signing into one deployment hands its session to the next one the app is pointed at —
 * a token minted by one tenant presented to another, and, in the ordinary case, a stale token
 * silently rejected by a deployment that never issued it. Slots are therefore keyed by address.
 *
 * The build-time address keeps the unscoped slots it has always used. That is not a special case
 * for its own sake: it means an installation that never chooses an address is byte-for-byte
 * unchanged, and nobody is signed out by this file existing.
 *
 * Scope boundary (the plan's, and worth restating where it would be tempting to cross): this
 * module makes an address *choosable and storable*. It does not discover deployments, does not
 * fetch a descriptor, and does not know what a tenant registry is.
 */
import { deviceLocal } from '@/privacy/deviceStorage';
import { WEB_ACCESS_TOKEN_SLOT, WEB_REFRESH_TOKEN_SLOT } from '@featit/shared/credentials';
import { defaultSchemeForHost } from '@featit/shared/desktop/private-host';
/*
 * Imported as a namespace so that the constant below can keep its name.
 *
 * This module has always exported `BUILT_IN_DEPLOYMENT_ADDRESS` and a hundred call sites import it
 * from here; renaming either side to avoid the collision would move the churn onto the readers
 * rather than removing it.
 */
import * as buildConstants from '@/buildConstants';
import { clearDesktopSession } from '@/desktop/desktopSessionLazy';

import { isDesktopRuntime } from '@/desktop/desktopDetection';

const DEPLOYMENT_ADDRESS_KEY = 'featit:deploymentAddress';
const DEPLOYMENT_REACHED_KEY = 'featit:deploymentReached';

// The slot names live in `@featit/shared` (T-0581), so a tool handing a browser a session writes them.
export const ACCESS_TOKEN_SLOT = WEB_ACCESS_TOKEN_SLOT;
export const REFRESH_TOKEN_SLOT = WEB_REFRESH_TOKEN_SLOT;

export interface ICredentialSlots {
  tokenKey: string;
  refreshKey: string;
}

/**
 * The scheme a bare address should be completed to, decided from its host.
 *
 * Parsed under https first only to read the hostname out; the result of that parse is discarded.
 * An address too malformed to parse falls back to https, which `normalizeDeploymentAddress` then
 * refuses on its own terms — this function never decides whether an address is valid, only which
 * scheme an otherwise-valid bare one deserves.
 */
function defaultSchemeForBareAddress(bare: string): 'http:' | 'https:' {
  try {
    return defaultSchemeForHost(new URL(`https://${bare}`).hostname);
  } catch {
    return 'https:';
  }
}

/**
 * The address baked in at build time, which is the default when nobody has chosen one.
 *
 * A build-time constant written by `web/vite.config.ts`, not an environment read: the app reads no
 * environment variable at all (ADR-073). Empty in every build served over http, because a page a
 * browser loaded from a deployment can see that deployment's origin in `window.location`; non-empty
 * only in a desktop build, whose pages come from `tauri://localhost` and which therefore has to be
 * told. `scripts/desktop-build.mjs` derives it from the channel and is the only writer.
 */
export const BUILT_IN_DEPLOYMENT_ADDRESS =
  buildConstants.BUILT_IN_DEPLOYMENT_ADDRESS.trim().replace(/\/+$/, '');

/**
 * Read a written address as a web address, or refuse it.
 *
 * A person types `featit.example.com`, not `https://featit.example.com/`, so a missing scheme is
 * completed rather than rejected — https for a public host, because a deployment carrying
 * credentials over plain http is a mistake nobody types deliberately, and **http for a private
 * one**, because a tailnet address cannot obtain a certificate at all and defaulting it to https
 * makes the dev deployment permanently unreachable. That is not hypothetical: `100.78.180.2` was
 * completed to `https://100.78.180.2`, dialled port 443, found nothing, and then reported
 * "Nothing answered at 100.78.180.2" — naming the address that was typed rather than the one that
 * was contacted, so the reader checks the host, finds it up, and is right. The rule is ADR-042's,
 * imported from `@featit/shared` rather than restated. Everything else is refused rather than
 * guessed at:
 * a query string or a fragment on a deployment address means the reader has pasted something other
 * than the address, and quietly discarding the part they pasted is how the wrong deployment gets
 * chosen without anybody noticing.
 */
export function normalizeDeploymentAddress(raw: string | null | undefined): string | null {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return null;
  /*
   * Inner whitespace is refused before parsing, because the URL parser does not refuse it: it
   * reads "not an address" as the host `not` with a percent-encoded path, and the app then goes
   * looking for a deployment on a host nobody named. Found by looking at the screen — the error
   * said "Nothing answered at not%20an%20address", which is the parser's answer, not a person's.
   */
  if (/\s/.test(trimmed)) return null;
  /*
   * The scheme depends on the host, and the host cannot be read without a scheme — so an explicit
   * address is taken as written, and a bare one is parsed once under https purely to learn its
   * hostname, then completed with whichever scheme that host warrants.
   */
  const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed);
  const withScheme = hasScheme ? trimmed : `${defaultSchemeForBareAddress(trimmed)}//${trimmed}`;

  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return null;
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
  if (!parsed.hostname) return null;
  if (parsed.search || parsed.hash) return null;
  if (parsed.username || parsed.password) return null;

  const path = parsed.pathname.replace(/\/+$/, '');
  return `${parsed.protocol}//${parsed.host}${path}`;
}

/**
 * The address the app was served from, which a browser reaches by construction.
 *
 * **Empty in the desktop shell, and that is the point.** A desktop build is not served by a
 * deployment; it renders one (ADR-050). Letting its own origin stand in for a deployment address
 * makes `isBuiltInDeploymentAddress` vouch for an address nothing can recover later, and
 * `storeDeploymentAddress` then declines to write it — leaving a desktop with no chosen address, no
 * built-in address, and a connect screen it returns to forever. Found by running the gate rather
 * than by reading it (I-0193): the browser clause and the desktop clause are each correct and were
 * jointly a loop.
 */
function servingAddress(): string {
  if (typeof window === 'undefined') return '';
  if (isDesktopRuntime()) return '';
  return normalizeDeploymentAddress(window.location.origin) ?? '';
}

/**
 * Is this the address this build already points at?
 *
 * Typing your own deployment's address into the chooser should be a no-op, not a sign-out. Without
 * this, an address that resolves to exactly where the app already talks would be filed under a new
 * slot, and the session already in hand would appear to vanish.
 */
export function isBuiltInDeploymentAddress(address: string | null): boolean {
  const normalized = normalizeDeploymentAddress(address);
  if (!normalized) return true;
  const builtIn = normalizeDeploymentAddress(BUILT_IN_DEPLOYMENT_ADDRESS);
  if (builtIn) return normalized === builtIn;
  return normalized === servingAddress();
}

function readStorage(key: string): string | null {
  try {
    return deviceLocal.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    deviceLocal.setItem(key, value);
  } catch {
    /* A browser that refuses storage cannot remember a choice; the session still works. */
  }
}

function removeStorage(key: string): void {
  try {
    deviceLocal.removeItem(key);
  } catch {
    /* See writeStorage. */
  }
}

/** The address somebody chose, or null when this installation has never been pointed anywhere. */
export function readChosenDeploymentAddress(): string | null {
  return normalizeDeploymentAddress(readStorage(DEPLOYMENT_ADDRESS_KEY));
}

export function hasChosenDeployment(): boolean {
  return readChosenDeploymentAddress() !== null;
}

/** The address this installation actually talks to: the chosen one, else the build-time one. */
export function effectiveDeploymentAddress(): string {
  return readChosenDeploymentAddress() ?? BUILT_IN_DEPLOYMENT_ADDRESS;
}

/**
 * Remember an address across restarts. Returns the normalized form, or null if it was refused.
 *
 * localStorage is what survives a restart in a browser tab, so the choice outliving the process is
 * a property of where it is written rather than of anything this module does.
 *
 * ⚠️ It is NOT what survives on the desktop, and this comment used to say it was. `tauri://localhost`
 * gets no persistent localStorage from WKWebView — measured: the app's `WebsiteData/LocalStorage`
 * directory is empty on a machine that has signed in many times. The credentials are covered by
 * `desktop/desktopSessionVault.ts`, which mirrors them to the OS keychain. **The address itself is
 * not**, and does not need to be: a desktop build carries its deployment as a build constant
 * (ADR-073), so a chosen address is an override that is re-chosen if it is lost, not the only route
 * to a working app.
 */
export function storeDeploymentAddress(raw: string): string | null {
  const normalized = normalizeDeploymentAddress(raw);
  if (!normalized) return null;
  if (isBuiltInDeploymentAddress(normalized)) {
    removeStorage(DEPLOYMENT_ADDRESS_KEY);
    return normalized;
  }
  writeStorage(DEPLOYMENT_ADDRESS_KEY, normalized);
  return normalized;
}

/**
 * Stop pointing at a deployment, and drop the credentials that belonged to it.
 *
 * The credentials go with it because "forget this deployment" is what somebody clicks when they no
 * longer want this machine holding a session there. Leaving the tokens behind under a slot nobody
 * can see any more would make that sentence false.
 */
export function forgetChosenDeployment(): void {
  const chosen = readChosenDeploymentAddress();
  if (chosen) {
    const slots = deploymentCredentialSlots(chosen);
    removeStorage(slots.tokenKey);
    removeStorage(slots.refreshKey);
    // The desktop keeps a copy in the OS keychain, and it outlives this process. Without this the
    // sentence in the comment above stops being true on the desktop: the address is forgotten, the
    // browser slots are emptied, and the next launch restores the session from the vault.
    void clearDesktopSession(chosen);
  }
  removeStorage(DEPLOYMENT_ADDRESS_KEY);
  removeStorage(DEPLOYMENT_REACHED_KEY);
}

/**
 * A readable, stable suffix for one address's storage slots.
 *
 * Readable rather than hashed on purpose: whoever is looking at storage while debugging a
 * two-deployment machine needs to see which slot belongs to which address. The scheme is part of it
 * because http and https to the same host are different deployments as far as a token is concerned.
 */
export function deploymentCredentialScope(address: string): string {
  return (normalizeDeploymentAddress(address) ?? address)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** The localStorage slots that hold this address's tokens, and no other address's. */
export function deploymentCredentialSlots(address: string | null): ICredentialSlots {
  if (isBuiltInDeploymentAddress(address)) {
    return { tokenKey: ACCESS_TOKEN_SLOT, refreshKey: REFRESH_TOKEN_SLOT };
  }
  const scope = deploymentCredentialScope(address as string);
  return {
    tokenKey: `${ACCESS_TOKEN_SLOT}:${scope}`,
    refreshKey: `${REFRESH_TOKEN_SLOT}:${scope}`,
  };
}

/**
 * Remember that an address actually answered, so the next start need not ask again.
 *
 * "An address is configured" and "an address works" are different facts, and only the second one
 * earns skipping the connect screen. Recording the first success is what lets the decision be made
 * synchronously on every later start — no probe, no spinner, no delay added to the ordinary path.
 */
export function markDeploymentReached(address: string): void {
  const normalized = normalizeDeploymentAddress(address);
  writeStorage(DEPLOYMENT_REACHED_KEY, normalized ?? '');
}

export function readReachedDeploymentAddress(): string | null {
  const stored = readStorage(DEPLOYMENT_REACHED_KEY);
  if (stored === '') return '';
  return normalizeDeploymentAddress(stored);
}

/** What to call an address in a sentence: the host, which is what a person recognises. */
export function deploymentAddressLabel(address: string | null | undefined): string {
  const normalized = normalizeDeploymentAddress(address);
  if (!normalized) return servingAddress().replace(/^https?:\/\//, '') || 'this deployment';
  return normalized.replace(/^https?:\/\//, '');
}
